<template>
    <div class="dashboard-wrapper">
        <CustomerSatisfactionFilterWidget @onChange="onChangeFilter" @onExport="onExport"/>
        <div class="dashboard-row">
            <div class="dashboard-card overall-rating">
              <p class="dashboard-title">Overall Rating</p>
              <NoResultsFound v-if="satisfactionSummaryData.surveyed < 1" class="no-result-content" />
              <div v-else class="overall-rating-content">
                <div class="main-detail">
                  <i class="bx bxs-star"></i>
                  <p>{{ satisfactionSummaryData.average_score.toFixed(1) }}</p>
                </div>
                <p class="overall-rating-footer">From</p>
                <div class="overall-rating-footer">
                  <i class="bx bx-group"></i>
                  <p><span class="overall-rating-number">{{ satisfactionSummaryData.surveyed }} </span>Respondents</p>
                </div>
              </div>
            </div>
            <div class="dashboard-card all-time-rating">
                <div class="all-time-rating">
                    <div class="header-widget">
                        <p>All Time Rating</p>
                    </div>
                        <NoResultsFound v-if="satisfactionSummaryData.surveyed <= 0" class="no-result-content"/>
                        <div v-else class="all-time-rating-content">
                          <div class="chart-content">
                            <GChart
                            id="bot-performance-chart"
                            class="g-chart"
                            type="PieChart"
                            :data="ratingChartData"
                            :options="chartOptions"
                            :events="chartEvents"
                            ></GChart>
                          </div>
                          <div v-if="surveyedNameToSatisfactionSummaryDetailMap.summaryStat !== null" class="rating-content">
                            <div v-for="(rating, index) in surveyedNameToSatisfactionSummaryDetailMap" :key="`rating-${index}`">
                              <div class="rating-detail">
                                <div class="rating-dot-color" v-bind:style="{color: rating.colorCode}">
                                  <i class='bx bxs-circle'></i>
                                </div>
                                <p>{{ rating.label }}</p>
                                <div class="topic-progress-bar">
                                  <div class="progress striped" v-bind:style="{ width: `${rating.summaryPercentageStat}%` }"></div>
                                </div>
                                <div class="rating-number">
                                  <p>{{ rating.summaryStat | number }}<span>&nbsp;</span>({{rating.summaryPercentageStat}}%)</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                </div>
            </div>
        </div>
        <div class="dashboard-row">
          <div class="dashboard-card satisfaction-percentage">
            <CustomerSatisfactionPercentageWidget
              :surveyedStat="satisfactionSummaryData.surveyed_stat"
              :respondents="satisfactionSummaryData.surveyed"
            />
          </div>
          <div class="dashboard-card inquiry-resolved">
            <div class="header-widget">
                <p>Was your inquiry resolved?</p>
            </div>
            <NoResultsFound v-if="noSurveyedResolution" class="no-result-content" />
            <CustomerInquiryResolvedWidget
              v-else
              :surveyed_resolution="satisfactionSummaryData.surveyed_stat.surveyed_resolution"
            />
          </div>
        </div>
        <div class="dashboard-row">
          <div class="dashboard-card customer-feedbacks">
            <div class="header-widget">
                <p>Customer feedbacks</p>
            </div>
            <CustomerFeedbacksWidget v-if="Array.isArray(customerFeedbackData.content)" v-bind:customerFeedbackData="customerFeedbackData" @selected-page="onSelectedPage($event)"/>
          </div>
        </div>
    </div>
</template>

<script>
import { GChart } from 'vue-google-charts';
import { DateTime } from 'luxon';
import CustomerFeedbacksWidget from '../DashboardWidgets/CustomerFeedbacksWidget.vue';
import CustomerSatisfactionPercentageWidget from '../DashboardWidgets/CustomerSatisfactionPercentageWidget.vue';
import CustomerInquiryResolvedWidget from '../DashboardWidgets/CustomerInquiryResolvedWidget.vue';
import NoResultsFound from '../NoResultsFound.vue';
import CustomerSatisfactionFilterWidget from '../DashboardWidgets/CustomerSatisfactionFilterWidget.vue';
import alice from '../../alice-api';
import { downloadFileHelper, getFilenameFromContentDispositionHelper } from '../../utiles/exportFileHelper';

export default {
  name: 'customer-satisdaction-dashboard',
  components: {
    CustomerFeedbacksWidget,
    CustomerSatisfactionPercentageWidget,
    CustomerInquiryResolvedWidget,
    CustomerSatisfactionFilterWidget,
    GChart,
    NoResultsFound
  },
  props: {
    criteria: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      currentFilters: [],
      currentPageFilter: 0,
      customerFeedbackData: {},
      satisfactionSummaryData: {},
      surveyedNameToSatisfactionSummaryDetailMap: {
        colorCode: '',
        summaryStat: null,
        summaryPercentageStat: null,
        name: '',
      },
      chartOptions: {
        backgroundColor: 'transparent',
        height: 200,
        width: 200,
        chartArea: {
          top: 50,
          left: 0,
        },
        colors: ['#EB0B0B', '#F8B300', '#F7DD00', '#89D421', '#44BC37'],
        legend: 'none',
        pieHole: 0.6,
        pieStartAngle: 270,
        pieSliceText: 'none',
        title: 'Fallback Rate',
        tooltip: {
          text: 'value',
        },
        slices: {
          5: {
            color: 'transparent',
          },
        },
        theme: 'maximized',
      },
      chartEvents: {
        ready: () => {
          // set width of label 0 - 100
          try {
            const chartWidth = document
              .querySelector('#bot-performance-chart g')
              .getBoundingClientRect().width;
            document.querySelector('.float-unit-content').style.width = `${chartWidth}px`;
          } catch (e) {
            // console.log('Cannot get chart width');
          }
          if (document.querySelector('#bot-performance-chart text') && document.querySelector('#bot-performance-chart text').innerHTML === 'No data') {
            document.querySelector('#bot-performance-chart text').style.opacity = '0.5';
          }
        },
      },
    };
  },
  methods: {
    async getSatisfaction(criteria) {
      if (criteria.channel) {
        this.prepareFilterData({
          field: 'channel',
          value: [criteria.channel],
        });
      }

      const sumData = await alice.getBrotherSatisfactionSummaryWithFilter({
        filter: this.currentFilters,
        dateFilter: {
          from: this.criteria.time.sinceDate,
          to: this.criteria.time.untilDate,
        },
        onlySurvey: true,
      });


      if (sumData?.data?.data) {
        this.satisfactionSummaryData = sumData.data.data;
        this.mapSurveyedStatsToDisplay();
      }

      const feedbackList = await alice.getBrotherSatisfactionWithFilter({
        filter: this.currentFilters,
        dateFilter: {
          from: this.criteria.time.sinceDate,
          to: this.criteria.time.untilDate,
        },
        onlySurvey: true,
        size: 10,
        page: this.currentPageFilter,
        sortBy: 'created_at',
        sortDirection: 'DESC',
      });

      if (feedbackList?.data?.data) {
        this.customerFeedbackData = feedbackList?.data?.data;
      }
    },
    mapSurveyedStatsToDisplay() {
      this.surveyedNameToSatisfactionSummaryDetailMap = {};
      // eslint-disable-next-line guard-for-in
      for (const rating in this.satisfactionSummaryData.surveyed_stat) {
        if (this.satisfactionSummaryData.surveyed_stat[rating] === undefined) {
          // eslint-disable-next-line no-continue
          continue;
        }
        switch (rating) {
          case 'very_satisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#44BC37',
              label: 'Very Satisfied'
            };
            break;
          case 'satisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#89D421',
              label: 'Satisfied'
            };
            break;
          // case 'ok':
          //  this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
          //    colorCode: '#F7DD00',
          //    label: 'Fair'
          //  };
          //  break;
          case 'dissatisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#F8B300',
              label: 'Dissatisfied'
            };
            break;
          case 'very_dissatisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#EB0B0B',
              label: 'Very Dissatisfied'
            };
            break;
          default:
            // eslint-disable-next-line no-continue
            continue;
        }
        this.surveyedNameToSatisfactionSummaryDetailMap[rating].summaryStat = this.satisfactionSummaryData.surveyed_stat[rating];
        this.surveyedNameToSatisfactionSummaryDetailMap[rating].summaryPercentageStat = this.satisfactionSummaryData.surveyed_stat[`percentage_${rating}`];
      }
    },
    prepareFilterData(filterObject) {
      /** The 'filterObject' structure is {field: string, value: [string|number] | null} */
      /** The 'this.currentFilters' is the state of Array of {field: string, value: [string|number]}
      the rule are in this array value in the object is not empty string and field in the object is unique */

      /** First Step: remove the null value that convert from empty string from the child component */
      if (!filterObject.value) {
        this.currentFilters = this.currentFilters.filter((filterItem) => filterItem.field !== filterObject.field);
        return;
      }

      /** Second Step: find and replace new selected value in the correct field if filter array already have this field */
      const filterIndex = this.currentFilters.findIndex((filterItem) => filterItem.field === filterObject.field);
      if (filterIndex > -1) {
        this.currentFilters[filterIndex].value = filterObject.value;
        return;
      }

      /** Third Step: if the field not exist just push it in filter array */
      this.currentFilters.push(filterObject);
    },
    async onSelectedPage(page) {
      this.currentPageFilter = page;
      await this.getSatisfaction(this.criteria);
    },
    async onChangeCriteria() {
      this.currentPageFilter = 0;
      await this.getSatisfaction(this.criteria);
    },
    async onChangeFilter(filterObject) {
      this.prepareFilterData(filterObject);
      await this.getSatisfaction(this.criteria);
    },
    async onExport() {
      const exportFilter = {
        filter: this.currentFilters,
        dateFilter: {
          from: this.criteria.time.sinceDate || DateTime.fromJSDate(new Date()).startOf('day').toUTC(),
          to: this.criteria.time.untilDate || DateTime.fromJSDate(new Date()).endOf('day').toUTC(),
        },
        sortBy: 'created_at',
        sortDirection: 'ASC',
        onlySurvey: true,
      };
      const response = await alice.exportSatisfaction(exportFilter);
      const filename = getFilenameFromContentDispositionHelper(response);
      downloadFileHelper(response.data, filename);
    },
  },
  computed: {
    ratingChartData() {
      const chartData = [['Rating', 'score']];
      if (this.satisfactionSummaryData.surveyed > 0) {
        const ratingLabels = {
          very_dissatisfied: 'Very Dissatisfied',
          dissatisfied: 'Dissatisfied',
          ok: 'Fair',
          satisfied: 'Satisfied',
          very_satisfied: 'Very Satisfied',
        };
        const ratingOrder = ['very_dissatisfied', 'dissatisfied', 'ok', 'satisfied', 'very_satisfied'];

        const surveyedStatData = ratingOrder.map((rating) => [
          ratingLabels[rating],
          this.satisfactionSummaryData.surveyed_stat[rating]
        ]);
        surveyedStatData.push([null, this.satisfactionSummaryData.surveyed]);
        return chartData.concat(surveyedStatData);
      }
      return chartData.concat([[null, 0]]);
    },
    noSurveyedResolution() {
      const resolutionValues = Object.values(this.satisfactionSummaryData.surveyed_stat.surveyed_resolution);
      return !resolutionValues.some((val) => val > 0);
    },
  },
  watch: {
    criteria: {
      handler: 'onChangeCriteria',
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.dashboard-wrapper{
    .dashboard-row{
        display: grid;
        grid-template-columns: repeat(8, minmax(0, 1fr));
        gap: 24px;
        padding-bottom: 24px;
        min-height: 140px;
        .dashboard-card{
            background-color: #FFFFFF;
            border-radius: 16px;
            padding: 44px 24px ;
            font-weight: 500;
            .dashboard-title{
            font-size: 20px;
            font-weight: 500;
            line-height: 24px;
            color: #4B5178;
            }
        }
        .header-widget{
            padding: 16px 16px 12px 16px;
            border-bottom: 1px solid #E9EBEC;
            font-size: 16px;
            font-weight: 500;
            line-height: 19.2px;
            color: #111B49;
            display: flex;
            justify-content: space-between;
            p {
                margin-bottom: 0px
            }
        }
        .dashboard-card.overall-rating{
            grid-column: span 2 / span 2;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            p {
              margin-bottom: 0px;
            }
            .overall-rating-content{
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                .main-detail{
                    display: flex;
                    justify-content: start;
                    align-items: center;
                    font-size: 40px;
                    font-weight: 500;
                    line-height: 48px;
                    color:#111B49;
                    padding: 20px 0px 24px 0px;
                    i {
                    color: #FCD14E;
                    font-size: 24px;
                    padding-right: 6px;
                    }
                }
                .overall-rating-footer{
                    display: flex;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 21px;
                    color: #646A8C;
                    i {
                        color: #59CBE8;
                        font-size: 24px;
                        margin-right: 8px;
                    }
                    .overall-rating-number{
                        font-weight: 500;
                    }
                }
            }
        }
        .dashboard-card.all-time-rating{
            grid-column: span 6 / span 6;
            padding: 0px;
            .all-time-rating-content{
              display: grid;
              grid-template-columns: repeat(8, minmax(0, 1fr));
              .chart-content{
                grid-column: span 3 / span 3;
                display: flex;
                align-items: end;
                justify-content: center;
              }
              .rating-content{
                grid-column: span 5 / span 5;
                display: flex;
                flex-direction: column;
                justify-content: center;
                padding: 38px;
                padding-left: 0px;
                .rating-detail{
                  display: flex;
                  align-items: center;
                  p{
                    margin-bottom: 0px;
                    flex: 1;
                    color: #4B5178;
                    font-weight: 400;
                  }
                  .rating-dot-color{
                    font-size: 8px;
                    padding-right: 8px;
                  }
                  .rating-number{
                    p{
                      width: 80px;
                      text-align: end;
                    }
                  }
                }
              }
            }
        }
        .dashboard-card.satisfaction-percentage{
          grid-column: span 4 / span 4;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .dashboard-card.inquiry-resolved{
          grid-column: span 4 / span 4;
          padding: 0px;
        }
        .dashboard-card.customer-feedbacks{
            grid-column: span 8 / span 8;
            padding: 0px;

        }
    }
    .topic-progress-bar {
      width: 100%;
      background-color: #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      height: 6px;
      margin-right: 20px;
      margin-left: 20px;
      flex: 2;
    }
    .progress {
      height: 100%;
      background-color: #3371FF;
      width: 0;
      border-radius: 8px;
    }
    .progress.striped::before {
      content: '';/* Pseudo-element for stripes */
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.1) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.1) 75%,
        transparent 75%,
        transparent 100%
      );
      background-size: 8px 8px;/* Size of the stripe pattern */
    }
    .no-result-content{
      margin-top: 40px;
      margin-bottom: 20px;
    }
}
</style>
